import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { useForgotPassword } from '../../../hooks/profile/useForgotPassword'

import Icon from '../../ui/Icon'
import { Button } from '../../buttons/Button'
import { TextInput } from '../../input/TextInput'
import { Notice } from '../notice/Notice'
import { Confirmation } from '../confirmation/Confirmation'

import { useApp } from '../../../contexts/AppContext'
import { FormField } from '../../../types/formData'
import { ForgotCredentials } from '../../../types/user/auth'
import { BannerKind } from '../../../types/contexts/AppContext'
import { InputType } from '../../../constants/inputTypes'

interface ForgotPasswordFormData {
  email: FormField<string>
}

export const ForgotPasswordForm = () => {
  const { t } = useTranslation(['forgot'])
  const navigate = useNavigate()
  const { showBanner } = useApp()
  const forgotPassword = useForgotPassword()
  const [formData, setFormData] = useState<ForgotPasswordFormData>({
    email: { value: '', isValid: false },
  })

  useEffect(() => {
    if (forgotPassword.error) {
      showBanner({
        kind: BannerKind.ERR,
        msg: forgotPassword.error,
      })
      forgotPassword.resetState()
    }
  }, [forgotPassword, showBanner])

  const handleInputChange = (name: string, value: any, isValid: boolean) => {
    setFormData(prev => ({ ...prev, [name]: { value, isValid } }))
  }

  const validForm = () => Object.values(formData).every(input => input.isValid)

  const handleSendLink = async () => {
    await forgotPassword.sendResetLink({
      email: formData.email.value,
    } as ForgotCredentials)
  }

  const sentConfirmationView = () => (
    <Confirmation
      icon={<Icon name="mail" size={28} />}
      title={t('forgot:success-title')}
      subtitle={t('forgot:success-subtitle')}
      notice={formData.email.value}
      prefixLinkLabel={t('forgot:success-expiry')}
      linkLabel={t('forgot:success-try-again')}
      onLinkClick={forgotPassword.resetState}
      submitLabel={t('forgot:success-back-login')}
      submitIcon={<Icon name="arrowR" size={16} />}
      onSubmit={() => navigate('/login')}
    />
  )

  const forgotPasswordView = () => (
    <form className="fly-form">
      <Button
        className="fly-btn-back"
        variant="ghost"
        size="sm"
        icon={<Icon name="chevL" size={14} />}
        label={t('forgot:back')}
        onClick={() => navigate('/')}
      />

      <div className="eyebrow">{t('forgot:eyebrow')}</div>
      <h1 className="display fly-text-title">{t('forgot:title')}</h1>

      <div className="ph-col">
        <p className="fly-text-subtitle">{t('forgot:subtitle')}</p>

        <TextInput
          name="email"
          label={t('auth:email')}
          required={true}
          type={InputType.Email}
          placeHolder={t('auth:email')}
          value={formData.email.value}
          onChange={handleInputChange}
          infoMessage={t('forgot:email-info')}
          errorMessage={t('forgot:email-error')}
        />

        <Button
          className="btn-submit"
          size="lg"
          block
          loading={forgotPassword.loading}
          disabled={!validForm()}
          label={
            forgotPassword.loading ? t('forgot:loading') : t('forgot:submit')
          }
          icon={<Icon name="arrowR" size={16} />}
          iconPosition="right"
          onClick={handleSendLink}
        />

        <Notice
          title={t('forgot:trouble-title')}
          icon={<Icon name="info" size={18} />}
        >
          {t('forgot:trouble-text')}
        </Notice>

        <p className="gfly-footer">
          {t('forgot:footer-text')}{' '}
          <Link to="/register">{t('forgot:footer-link')}</Link>
        </p>
      </div>
    </form>
  )

  return forgotPassword.success ? sentConfirmationView() : forgotPasswordView()
}
